import { useState } from 'react'
import { MessageSquarePlus, X } from 'lucide-react'
import { useApp } from '../context/useApp'
import { useToast } from '../context/useToast'
import { submitFeedback } from '../api'
import { logError } from '../utils/logError'

const CATEGORIES = [
  { value: 'idea', label: 'Idea' },
  { value: 'bug', label: 'Bug' },
  { value: 'confusing', label: 'Confusing' },
  { value: 'other', label: 'Other' },
]

export default function FeedbackWidget() {
  const { user } = useApp()
  const { showSuccess, showError } = useToast()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState('idea')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('') // honeypot
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!message.trim() || submitting) return
    setSubmitting(true)
    try {
      await submitFeedback({
        kind: 'general',
        category,
        message: message.trim(),
        email: user ? undefined : (email.trim() || undefined),
        pageUrl: window.location.pathname,
        company,
      })
      showSuccess('Thanks for the feedback!')
      setOpen(false)
      setMessage('')
      setEmail('')
    } catch (err) {
      logError('feedback.submit', err)
      showError('Could not send feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open feedback form"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-gold hover:bg-gold-light text-white px-4 py-2.5 rounded-full shadow-lg border-0 cursor-pointer transition-colors"
      >
        <MessageSquarePlus className="w-4 h-4" /> <span className="text-sm font-semibold">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            className="w-full max-w-md bg-navy-card border border-slate-border rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg text-cream">Send feedback</h2>
              <button type="button" aria-label="Close feedback" onClick={() => setOpen(false)} className="text-sepia hover:text-parchment bg-transparent border-0 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-xs text-sepia mb-1">Type</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mb-4 px-3 py-2 bg-navy-lighter border border-slate-border rounded-lg text-cream text-sm focus:outline-none focus:border-gold/50"
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              rows={4}
              className="w-full mb-4 px-3 py-2 bg-navy-lighter border border-slate-border rounded-lg text-cream text-sm placeholder-sepia/50 focus:outline-none focus:border-gold/50"
            />

            {!user && (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional — if you'd like a reply)"
                className="w-full mb-4 px-3 py-2 bg-navy-lighter border border-slate-border rounded-lg text-cream text-sm placeholder-sepia/50 focus:outline-none focus:border-gold/50"
              />
            )}

            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="hidden"
              aria-hidden="true"
            />

            <button
              type="submit"
              disabled={!message.trim() || submitting}
              className="w-full bg-gold hover:bg-gold-light disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-semibold border-0 cursor-pointer transition-colors"
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>
          </form>
        </div>
      )}
    </>
  )
}
